import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import './ImportRow.css';

export type ImportRowState = 'queued' | 'active' | 'done' | 'failed';

/**
 * DESIGN.md §10 ImportRow: thumb (source icon) · title (URL until the title is known) · status
 * line · 5-segment pipeline. `step` is the active segment (0–4); the status is a live region.
 */
export function ImportRow({
  icon,
  title,
  status,
  state,
  step,
  action,
}: {
  icon: IconName;
  title: string;
  status: string;
  state: ImportRowState;
  step: number;
  action?: ReactNode;
}) {
  const statusIcon: IconName | null =
    state === 'queued' ? 'clock' : state === 'done' ? 'check' : state === 'failed' ? 'alert' : null;
  return (
    <li
      className={`import-row${state === 'done' ? ' is-done' : state === 'failed' ? ' is-failed' : ''}`}
    >
      <span className="import-row__thumb">
        <Icon name={icon} />
      </span>
      <div className="import-row__body">
        <span className="import-row__title">{title}</span>
        <div
          className={`pipeline${state === 'done' ? ' is-done' : state === 'failed' ? ' is-failed' : ''}`}
          aria-hidden="true"
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={`pipeline__seg${
                state === 'done' || i < step
                  ? ' is-done'
                  : i === step && state !== 'queued'
                    ? ' is-active'
                    : ''
              }`}
            />
          ))}
        </div>
        <span className="import-row__status">
          {state === 'active' && <span className="spinner" aria-hidden="true" />}
          {statusIcon && <Icon name={statusIcon} size="s" />}
          {status}
        </span>
      </div>
      {action}
    </li>
  );
}
